import fetch, { getUrlObject } from '../libs/fetch'
import { PREFERENCE_SERVICE_URL_KEY, TSG_ID_NONE } from './constants'
import { getFrameworkServiceUrl } from './states'
import { getCurrentTsgId, getTsgToken } from './tsg'
import { tryParseErrorJson } from './utils'

export const COMMON_THEME = 'common.theme'
export const COMMON_FAVORITES = 'common.favorites'
export const COMMON_LAST_VISITED_TSG_ID = 'common.last_visited_tsg_id'

// TODO Fetch Every 10 mins
export type PreferenceType = {
  init: (token?: string) => Promise<boolean>
  get: <T = any>(key: string) => T | undefined
  set: (key: string, value: any, options?: PreferenceOptions) => Promise<boolean>
  result: any
  environment: number
}
export type PreferenceOptions = {
  token?: string
  tsg_id?: string
}

const tryParseJsonValue = (text: string | undefined) => {
  if (text && /^[{["]|^(?:true|false|\d+)$/.test(text.trim())) {
    try {
      return JSON.parse(text)
    }
    catch (e) {
      return text
    }
  }
  return text
}
const genHeaders = (token: string) => ({
  'authorization': `Bearer ${token}`,
  'Content-Type': 'application/json',
})

const preference: PreferenceType = {
  result: {},
  environment: 0,
  async init(token?: string): Promise<boolean> {
    const data = JSON.stringify({
      'identifier': 'IAM_USER'
    })

    const url = getUrlObject({
      baseUrl: getFrameworkServiceUrl(PREFERENCE_SERVICE_URL_KEY)!,
      path: '/identities/',
    })!

    const resp = await fetch(url.toString(), {
      method: 'POST',
      headers: genHeaders(token || await getTsgToken(TSG_ID_NONE)),
      body: data,
    }).then(async (res) => {
      if (res.ok) {
        const data = (await res.json()) as any
        return data
      }
      const data = await res.text().then(tryParseErrorJson)
      console.error('failed to initialize user preference', data)
      return false
    }, (error: Error) => {
      console.error('failed to initialize preference service', error)
      return false
    })

    if (!resp?.flags) {
      return false
    }

    preference.result = resp.flags.reduce((obj: any, item: any) => {
      obj[item.feature.name] = {
        value: item.feature_state_value,
        state_id: item.id,
        feature_id: item.feature.id,
        identity: !!item.identity
      }
      preference.environment = item.environment
      return obj
    }, {})

    return true
  },
  async set(key: string, value: any, { token, tsg_id }: PreferenceOptions = {}): Promise<boolean> { // TODO: option for tsg

    if (!preference?.result) {
      console.error('Preference Service not initialized')
      return false
    }

    if (!(key in preference.result)) {
      console.error('Preference does not exist, please find out supported preferences', key)
      return false
    }

    const jwt = token || await getTsgToken(tsg_id || getCurrentTsgId())
    // Feature Identity Based
    if (preference.result[key].identity) {

      const data = JSON.stringify({
        environment: preference.environment,
        feature: preference.result[key].feature_id,
        feature_state_value: {
          string_value: JSON.stringify(value)
        },
        multivariate_feature_state_values: [],
        enabled: true
      })

      const url = getUrlObject({
        baseUrl: getFrameworkServiceUrl(PREFERENCE_SERVICE_URL_KEY)!,
        path: `/features/featurestates/${preference.result[key].state_id}/`,
      })!

      await fetch(url.toString(), {
        method: 'PUT',
        headers: genHeaders(jwt),
        body: data,
      }).then(async (res) => {
        if (res.ok) {
          const data = (await res.json()) as any
          return data
        }
        const data = await res.text().then(tryParseErrorJson)
        console.error('failed to set featurestates PUT', data)
        return false
      }, (error: Error) => {
        console.error('failed to set featurestates PUT', error)
        return false
      })

    }
    else { // Need to make feature identity based

      const data = JSON.stringify({
        environment: preference.environment,
        feature: preference.result[key].feature_id,
        feature_state_value: {
          string_value: JSON.stringify(value)
        },
        multivariate_feature_state_values: [],
        enabled: true,
        identifier: 'IAM_USER'
      })

      const url = getUrlObject({
        baseUrl: getFrameworkServiceUrl(PREFERENCE_SERVICE_URL_KEY)!,
        path: '/features/featurestates/',
      })!

      await fetch(url.toString(), {
        method: 'POST',
        headers: genHeaders(jwt),
        body: data,
      }).then(async (res) => {
        if (res.ok) {
          const data = (await res.json()) as any
          return data
        }
        const data = await res.text().then(tryParseErrorJson)
        console.error('failed to set featurestates POST', data)
        return false
      }, (error: Error) => {
        console.error('failed to set featurestates POST', error)
        return false
      })

    }

    return preference.init(jwt)
  },
  get(key: string): any { // TODO: option for tsg
    if (!preference?.result) {
      console.error('Preference Service not initialized')
    }
    else if (key in preference.result) {
      return tryParseJsonValue(preference.result[key].value)
    }
    return undefined
  },
}

export default preference
